import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// Layouts
import LoginLayout from "./layouts/LoginLayout";
import PublicLayout from "./layouts/PublicLayout";
import AuthenticatedLayout from "./layouts/AuthenticatedLayout";

// Auth Pages (No Header/Footer)
import Login from "./pages/Login";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import ActivateLogisticsProviderPage from "./pages/ActivateLogisticsProviderPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";

// Dashboard Pages
import BuyerDashboard from "./pages/dashboard/BuyerDashboard";
import SellerDashboard from "./pages/dashboard/SellerDashboard";
import BuyerContractManufacturersPage from "./pages/dashboard/BuyerContractManufacturersPage";
import BuyerContractManufacturerQuotesPage from "./pages/dashboard/BuyerContractManufacturerQuotesPage";
import BuyerContractManufacturerAgreementCreatePage from "./pages/dashboard/BuyerContractManufacturerAgreementCreatePage";
import BuyerContractManufacturerSignAgreementPage from "./pages/dashboard/BuyerContractManufacturerSignAgreementPage";
import BuyerCmQualityApprovalPage from "./pages/dashboard/BuyerCmQualityApprovalPage";
import BuyerRecruitExternalQaPage from "./pages/dashboard/BuyerRecruitExternalQaPage";
import SellerContractManufacturerRfqsPage from "./pages/dashboard/SellerContractManufacturerRfqsPage";
import SellerContractManufacturerRfqQuotePage from "./pages/dashboard/SellerContractManufacturerRfqQuotePage";
import SellerContractManufacturerAgreementPage from "./pages/dashboard/SellerContractManufacturerAgreementPage";
import SellerCmInternalInspectionReportPage from "./pages/dashboard/SellerCmInternalInspectionReportPage";
import SellerCmProductionInvoicePage from "./pages/dashboard/SellerCmProductionInvoicePage";
import SellerCmProductionEwayPage from "./pages/dashboard/SellerCmProductionEwayPage";
import SellerCmProductionEwayTrackingPage from "./pages/dashboard/SellerCmProductionEwayTrackingPage";
import SellerCmProductionShippingManifestPage from "./pages/dashboard/SellerCmProductionShippingManifestPage";
import SellerCmProductionReviewGrnPage from "./pages/dashboard/SellerCmProductionReviewGrnPage";
import BuyerCmProductionWorkspacePage from "./pages/dashboard/BuyerCmProductionWorkspacePage";
import BuyerCmDeliveryConfirmationPage from "./pages/dashboard/BuyerCmDeliveryConfirmationPage";
import BuyerCmProductTrackingPage from "./pages/dashboard/BuyerCmProductTrackingPage";
import LogisticsRfqSentListPage from "./pages/dashboard/LogisticsRfqSentListPage";
import LogisticsDedicatedRfqSubmittedPage from "./pages/website/LogisticsDedicatedRfqSubmittedPage";
import LogisticsProviderDashboard from "./pages/dashboard/LogisticsProviderDashboard";
import LogisticsProviderRfqsPage from "./pages/dashboard/LogisticsProviderRfqsPage";
import LogisticsProviderQuoteSubmissionPage from "./pages/dashboard/LogisticsProviderQuoteSubmissionPage";
import LogisticsCompareQuotesPage from "./pages/dashboard/LogisticsCompareQuotesPage";
import LogisticsQuoteDetailsPage from "./pages/dashboard/LogisticsQuoteDetailsPage";
import LogisticsHandOverPage from "./pages/dashboard/LogisticsHandOverPage";
import LogisticsPickupConfirmationPage from "./pages/dashboard/LogisticsPickupConfirmationPage";
import LogisticsShipmentTransitPage from "./pages/dashboard/LogisticsShipmentTransitPage";
import LogisticsGenerateInvoicePage from "./pages/dashboard/LogisticsGenerateInvoicePage";

// Protected Pages (With Header/Footer)
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ViewProfile from "./pages/Profile Management/ViewProfile";
import SellerProfile from "./pages/Profile Management/seller_profile";
import ContractManufacturerProfile from "./pages/Profile Management/contract_manufacturer_profile";
import BuyerProfile from "./pages/Profile Management/buyer_profile";
import BuyerSellerProfile from "./pages/Profile Management/buyer_seller_profile";
import QualityInspectorProfile from "./pages/Profile Management/quality_inspector_profile";
import LogisticsProviderProfile from "./pages/Profile Management/logistics_provider_profile";
import LogisticsProviderProfileView from "./pages/Profile Management/logistics_provider_profile_view";
import BuyerRFQForm from "./pages/buyer seller/Buyer/buyer_rfq_form";
import BuyerRfq from "./pages/buyer seller/Buyer/buyer_rfq";
import SellerRFQList from "./pages/buyer seller/Seller/buyer_rfq_list";
import ClarificationList from "./pages/buyer seller/Seller/clarification_list";
import BuyerClarificationList from "./pages/buyer seller/Buyer/buyer_clarification_list";
import SubmitQuotationToBuyer from "./pages/buyer seller/Seller/submit_quotation_to_buyer";
import SellerQuotationList from "./pages/buyer seller/Buyer/seller_quotation_list";
import SellerQuotationAccept from "./pages/buyer seller/Buyer/Seller_quotation_accept";
import BuyerQARequest from "./pages/buyer seller/Buyer/buyer_qa_request";
import QuotationQARequestSuccess from "./pages/buyer seller/Buyer/quotation_qa_request_success";
import BuyerQAQuotationsList from "./pages/buyer seller/Buyer/buyer_qa_quotations_list";
import BuyerQAInspectionReview from "./pages/buyer seller/Buyer/buyer_qa_inspection_review";
import SampleRequest from "./pages/buyer seller/Buyer/sample_request";
import POCreation from "./pages/buyer seller/Buyer/po_creation";
import POStatusTracking from "./pages/buyer seller/Buyer/po_status_tracking";
import PurchaseOrderList from "./pages/buyer seller/Buyer/purchase_order_list";
import SellerPurchaseOrderList from "./pages/buyer seller/Seller/seller_purchase_order_list";
import PurchaseOrderAccept from "./pages/buyer seller/Seller/purchase_order_accept";
import EditPurchaseOrder from "./pages/buyer seller/Buyer/edit_purchase_order";
import GenerateInvoice from "./pages/buyer seller/Seller/generate_invoice";
import InvoiceList from "./pages/buyer seller/Seller/invoice_list";
import EwayGenerationList from "./pages/buyer seller/Seller/eway_generation_list";
import ShippingManifestGenerationList from "./pages/buyer seller/Seller/shipping_manifest_generation_list";
import ShippingManifestCreate from "./pages/buyer seller/Seller/shipping_manifest_create";
import ShippingManifestList from "./pages/buyer seller/Buyer/shipping_manifest_list";
import GRNManifestList from "./pages/buyer seller/Buyer/grn_manifest_list";
import BuyerGoodsReceiptGenerate from "./pages/buyer seller/Buyer/buyer_goods_receipt_generate";
import ApproveGoodsReceiptList from "./pages/buyer seller/Seller/approve_goods_receipt_list";
import ApproveInvoiceList from "./pages/buyer seller/Buyer/approve_invoice_list";
import EWayBillGenerate from "./pages/buyer seller/Seller/eway_bill_generate";
import EWayBillTracking from "./pages/buyer seller/Buyer/eway_bill_tracking";

// Auction Pages
import AuctionList from "./pages/auction/AuctionList";
import AuctionDetail from "./pages/auction/AuctionDetail";
import PurchaseCredits from "./pages/auction/PurchaseCredits";
import CreditPurchaseSuccess from "./pages/auction/CreditPurchaseSuccess";
import CreateAuction from "./pages/auction/CreateAuction";
import MyAuctions from "./pages/auction/MyAuctions";
import MyBids from "./pages/auction/MyBids";
import BidSuccess from "./pages/auction/BidSuccess";
import ReviewBids from "./pages/auction/ReviewBids";
import WinningBidReview from "./pages/auction/WinningBidReview";
import AuctionPayment from "./pages/auction/AuctionPayment";
import ProformaInvoice from "./pages/auction/ProformaInvoice";
import PartyConnection from "./pages/auction/PartyConnection";
import PaymentProcess from "./pages/auction/PaymentProcess";
import PaymentGateway from "./pages/auction/PaymentGateway";
import TransactionCompletion from "./pages/auction/TransactionCompletion";
import CollectionDeliveryConfirmation from "./pages/auction/CollectionDeliveryConfirmation";
import QualityEvaluatorPrompt from "./pages/auction/QualityEvaluatorPrompt";
import RequestInspectionQuotation from "./pages/auction/RequestInspectionQuotation";
import RFQSentSuccess from "./pages/auction/RFQSentSuccess";
import InspectionRFQDetails from "./pages/auction/InspectionRFQDetails";
import ReviewInspectorQuotations from "./pages/auction/ReviewInspectorQuotations";
import InspectionInitiated from "./pages/auction/InspectionInitiated";
import InspectionCompletion from "./pages/auction/InspectionCompletion";
import AcceptRejectProduct from "./pages/auction/AcceptRejectProduct";
import ProductRejected from "./pages/auction/ProductRejected";
import QADashboard from "./pages/auction/QADashboard";
import QARequestsList from "./pages/auction/QARequestsList";
import QARequestQuotationDetail from "./pages/auction/QARequestQuotationDetail";
import QuotationFlowInspectorQuotation from "./pages/auction/QuotationFlowInspectorQuotation";
import QAInspectionsList from "./pages/auction/QAInspectionsList";
import QACompletedList from "./pages/auction/QACompletedList";
import QAEscrowPayment from "./pages/auction/QAEscrowPayment";
import QAPaymentSuccess from "./pages/auction/QAPaymentSuccess";
import QAAssignmentAccept from "./pages/auction/QAAssignmentAccept";

// Website Pages
import Home from "./pages/website/Home";
import ProductMarketplacePage from "./pages/website/ProductMarketplacePage";
import ProductMarketplaceDetailPage from "./pages/website/ProductMarketplaceDetailPage";
import ProductMarketplaceCheckoutPage from "./pages/website/ProductMarketplaceCheckoutPage";
import MyCartPage from "./pages/website/MyCartPage";
import CartCheckoutPage from "./pages/website/CartCheckoutPage";
import MyWishlistPage from "./pages/website/MyWishlistPage";
import MySavedListsPage from "./pages/website/MySavedListsPage";
import HubHomeLayout from "./layouts/HubHomeLayout";
import Contact from "./pages/website/Contact";
import ContractManufacturersBrowsePage from "./pages/website/ContractManufacturersBrowsePage";
import ContractManufacturerPublicProfile from "./pages/website/ContractManufacturerPublicProfile";
import ContractManufacturerRfqPage from "./pages/website/ContractManufacturerRfqPage";
import LogisticsProvidersBrowsePage from "./pages/website/LogisticsProvidersBrowsePage";
import LogisticsRfqPlaceholderPage from "./pages/website/LogisticsRfqPlaceholderPage";
import LogisticsRfqSelectionPage from "./pages/website/LogisticsRfqSelectionPage";
import LogisticsRfqMatchProvidersPage from "./pages/website/LogisticsRfqMatchProvidersPage";
import LogisticsRfqSubmittedViewPage from "./pages/website/LogisticsRfqSubmittedViewPage";
import ContractManufacturerRfqSubmittedViewPage from "./pages/website/ContractManufacturerRfqSubmittedViewPage";
import InfrastructureLeasingListAssetPage from "./pages/website/InfrastructureLeasingListAssetPage";
import InfrastructureLeasingPostRequirementPage from "./pages/website/InfrastructureLeasingPostRequirementPage";
import InfrastructureLeasingPublicListingPage from "./pages/website/InfrastructureLeasingPublicListingPage";
import InfrastructureLeasingBrowsePage from "./pages/website/InfrastructureLeasingBrowsePage";
import InfrastructureLeasingSearchListingsPage from "./pages/website/InfrastructureLeasingSearchListingsPage";
import ProcurementServicesPublicPage from "./pages/website/ProcurementServicesPublicPage";
import InfrastructureLeasingMyPropertiesPage from "./pages/website/InfrastructureLeasingMyPropertiesPage";
import InfrastructureLeasingSellerInventoryOverviewPage from "./pages/website/InfrastructureLeasingSellerInventoryOverviewPage";
import InfrastructureLeasingSellerInventoryHubPage from "./pages/website/InfrastructureLeasingSellerInventoryHubPage";
import InfrastructureLeasingSellerListingEnquiriesPage from "./pages/website/InfrastructureLeasingSellerListingEnquiriesPage";
import InfrastructureLeasingSellerBuyerRequirementViewPage from "./pages/website/InfrastructureLeasingSellerBuyerRequirementViewPage";
import InfrastructureLeasingSellerPaymentInvoicePage from "./pages/website/InfrastructureLeasingSellerPaymentInvoicePage";
import InfrastructureLeasingAgreementDetailsPage from "./pages/website/InfrastructureLeasingAgreementDetailsPage";
import InfrastructureLeasingBuyerEnquiriesPage from "./pages/website/InfrastructureLeasingBuyerEnquiriesPage";
import InfrastructureLeasingBuyerPaymentPage from "./pages/website/InfrastructureLeasingBuyerPaymentPage";
import InfrastructureLeasingBuyerInvoicePage from "./pages/website/InfrastructureLeasingBuyerInvoicePage";
import InfrastructureLeasingBuyerLeaseTrackingPage from "./pages/website/InfrastructureLeasingBuyerLeaseTrackingPage";
import InfrastructureLeasingBuyerRequirementsPage from "./pages/website/InfrastructureLeasingBuyerRequirementsPage";
import InfrastructureLeasingBuyerRequirementDetailPage from "./pages/website/InfrastructureLeasingBuyerRequirementDetailPage";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import SuperAdminProtectedRoute from "./components/SuperAdminProtectedRoute";
import SuperAdminLoginLayout from "./layouts/SuperAdminLoginLayout";
import SuperAdminLayout from "./layouts/SuperAdminLayout";
import SuperAdminLoginPage from "./pages/super-admin/SuperAdminLoginPage";
import SuperAdminDashboardPage from "./pages/super-admin/SuperAdminDashboardPage";
import SuperAdminHomeSectionManagementPage from "./pages/super-admin/SuperAdminHomeSectionManagementPage";
import SuperAdminServiceQuickLinksPage from "./pages/super-admin/SuperAdminServiceQuickLinksPage";
import SuperAdminTradingCategoriesPage from "./pages/super-admin/SuperAdminTradingCategoriesPage";
import SuperAdminFeaturedCategoryShowcasePage from "./pages/super-admin/SuperAdminFeaturedCategoryShowcasePage";
import SuperAdminTopIndustriesPage from "./pages/super-admin/SuperAdminTopIndustriesPage";
import SuperAdminAuthHeroManagementPage from "./pages/super-admin/SuperAdminAuthHeroManagementPage";
import SuperAdminPlatformLogoManagementPage from "./pages/super-admin/SuperAdminPlatformLogoManagementPage";
import SuperAdminCmsDashboardPage from "./pages/super-admin/SuperAdminCmsDashboardPage";
import SuperAdminCmsPageEditorPage from "./pages/super-admin/SuperAdminCmsPageEditorPage";
import SuperAdminTermsManagementPage from "./pages/super-admin/SuperAdminTermsManagementPage";
import SuperAdminUnauthorizedPage from "./pages/super-admin/SuperAdminUnauthorizedPage";
import SuperAdminUserManagementPage from "./pages/super-admin/SuperAdminUserManagementPage";
import SuperAdminCreateUserPage from "./pages/super-admin/SuperAdminCreateUserPage";
import SuperAdminRoleManagementPage from "./pages/super-admin/SuperAdminRoleManagementPage";
import SuperAdminCreateRolePage from "./pages/super-admin/SuperAdminCreateRolePage";
import SuperAdminDisputeManagementPage from "./pages/super-admin/SuperAdminDisputeManagementPage";
import BusinessRelocationRfqsManagementPage from "./pages/super-admin/BusinessRelocationRfqsManagementPage";
import ExhibitionLogisticsRfqsManagementPage from "./pages/super-admin/ExhibitionLogisticsRfqsManagementPage";
import InviteLogisticsProvidersPage from "./pages/super-admin/InviteLogisticsProvidersPage";
import SuperAdminAssignedDisputeDetailPage from "./pages/super-admin/SuperAdminAssignedDisputeDetailPage";
import SuperAdminTicketManagementPage from "./pages/super-admin/SuperAdminTicketManagementPage";
import SuperAdminTicketDetailPage from "./pages/super-admin/SuperAdminTicketDetailPage";
import About from "./pages/website/About";
import PrivacyPolicy from "./pages/website/PrivacyPolicy";
import RefundCancellationPolicy from "./pages/website/RefundCancellationPolicy";
import DomainRedirectionPolicy from "./pages/website/DomainRedirectionPolicy";
import PublicCmsPage from "./pages/website/PublicCmsPage";
import PublicTermsPage from "./pages/website/PublicTermsPage";
import HelpCenter from "./pages/website/HelpCenter";
import HelpCenterArticlesPage from "./pages/website/HelpCenterArticlesPage";
import HelpCenterArticleDetailPage from "./pages/website/HelpCenterArticleDetailPage";
import HelpCenterTopicPage from "./pages/website/HelpCenterTopicPage";
import SubmitDisputePage from "./pages/website/SubmitDisputePage";
import RaiseSupportTicketPage from "./pages/website/RaiseSupportTicketPage";
import TicketCreatedSuccessPage from "./pages/website/TicketCreatedSuccessPage";
import FeedbackPage from "./pages/website/FeedbackPage";
import FeedbackSubmittedSuccessPage from "./pages/website/FeedbackSubmittedSuccessPage";
import SupportAssignedDisputesPage from "./pages/dashboard/SupportAssignedDisputesPage";
import SupportDisputeDetailPage from "./pages/dashboard/SupportDisputeDetailPage";
import SupportAssignedTicketsPage from "./pages/dashboard/SupportAssignedTicketsPage";
import SupportTicketDetailPage from "./pages/dashboard/SupportTicketDetailPage";
import SupportAssignedFeedbackPage from "./pages/dashboard/SupportAssignedFeedbackPage";
import SupportFeedbackDetailPage from "./pages/dashboard/SupportFeedbackDetailPage";
import MyDisputesPage from "./pages/dashboard/MyDisputesPage";
import MyDisputeDetailPage from "./pages/dashboard/MyDisputeDetailPage";
import MyTicketsPage from "./pages/dashboard/MyTicketsPage";
import MyTicketDetailPage from "./pages/dashboard/MyTicketDetailPage";
import MyFeedbackPage from "./pages/dashboard/MyFeedbackPage";
import MyFeedbackDetailPage from "./pages/dashboard/MyFeedbackDetailPage";
import SuperAdminFeedbackManagementPage from "./pages/super-admin/SuperAdminFeedbackManagementPage";
import SuperAdminFeedbackDetailPage from "./pages/super-admin/SuperAdminFeedbackDetailPage";
import FaqPage from "./pages/website/FaqPage";
import ComingSoonPage from "./pages/website/ComingSoonPage";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import NotFound from "./pages/website/NotFound";
import {
  RequireSuperAdmin,
  RequireP2PPlatformAdmin,
  RequireP2PTeamRole,
  RequireCompanyAdmin,
  RequireCompanyMember,
  RequireCompanyApprover,
  RequireCommandCentreAccess,
  RequireServiceRequestAccess,
  RequirePlatformServiceConsole,
} from "./components/p2p/guards/P2PRouteGuards";

const InfrastructureLeasingAdminDisputesPage = lazy(() => import("./pages/dashboard/InfrastructureLeasingAdminDisputesPage"));
const P2POrganizationSetupPage = lazy(() => import("./pages/p2p/P2POrganizationSetupPage"));
const P2PApprovalSetupRoute = lazy(() => import("./pages/p2p/P2PApprovalSetupRoute"));
const P2PAccessDiagnosticsPage = lazy(() => import("./pages/p2p/P2PAccessDiagnosticsPage"));
const MyApprovalsPage = lazy(() => import("./pages/p2p/MyApprovalsPage"));
const NotificationsPage = lazy(() => import("./pages/dashboard/NotificationsPage"));
const ProcurementCommandCentrePage = lazy(() =>
  import("./pages/p2p/ProcurementCommandCentrePage")
);
const P2PTransactionDetailPage = lazy(() => import("./pages/p2p/P2PTransactionDetailPage"));
const PreApprovedVendorsPage = lazy(() => import("./pages/p2p/PreApprovedVendorsPage"));
const ProcurementServiceCataloguePage = lazy(() =>
  import("./pages/p2p/ProcurementServiceCataloguePage")
);
const PlatformServiceCatalogueAdminPage = lazy(() =>
  import("./pages/p2p/PlatformServiceCatalogueAdminPage")
);
const P2PPlatformAdminManagementPage = lazy(() =>
  import("./pages/p2p/P2PPlatformAdminManagementPage")
);
const ProcurementServiceConsolePage = lazy(() =>
  import("./pages/p2p/ProcurementServiceConsolePage")
);
const P2PInsightsPage = lazy(() => import("./pages/p2p/P2PInsightsPage"));
const MasterCategoryAdminPage = lazy(() => import("./pages/dashboard/MasterCategoryAdminPage"));
const LocationMasterAdminPage = lazy(() => import("./pages/dashboard/LocationMasterAdminPage"));
const FactoryMasterAdminPage = lazy(() => import("./pages/dashboard/FactoryMasterAdminPage"));
const CmIndustryTypeAdminPage = lazy(() => import("./pages/dashboard/CmIndustryTypeAdminPage"));
const CmIndustryTypeBulkUploadPage = lazy(() => import("./pages/dashboard/CmIndustryTypeBulkUploadPage"));
const FacilityCategoryAdminPage = lazy(() => import("./pages/dashboard/FacilityCategoryAdminPage"));
const FacilityCategoryBulkUploadPage = lazy(() => import("./pages/dashboard/FacilityCategoryBulkUploadPage"));
const AuctionCategoryAdminPage = lazy(() => import("./pages/dashboard/AuctionCategoryAdminPage"));
const AuctionCategoryBulkUploadPage = lazy(() => import("./pages/dashboard/AuctionCategoryBulkUploadPage"));
const MasterCategoryBulkUploadPage = lazy(() => import("./pages/dashboard/MasterCategoryBulkUploadPage"));
const ProductBulkUploadPage = lazy(() => import("./pages/dashboard/ProductBulkUploadPage"));
const BannerManagementPage = lazy(() => import("./pages/dashboard/BannerManagementPage"));
const ModuleLogisticsIntegrationConfigPage = lazy(() => import("./pages/dashboard/ModuleLogisticsIntegrationConfigPage"));
const ModuleLogisticsBrowserSmokePage = lazy(() => import("./pages/dashboard/ModuleLogisticsBrowserSmokePage"));
const InfrastructureOperationsLayout = lazy(() => import("./pages/dashboard/InfrastructureOperationsLayout"));
const InfrastructureOperationsDashboardPage = lazy(() => import("./pages/dashboard/InfrastructureOperationsDashboardPage"));
const InfrastructureOperationsSectionPage = lazy(() => import("./pages/dashboard/InfrastructureOperationsSectionPage"));
const InfrastructureReportsLayout = lazy(() => import("./pages/dashboard/InfrastructureReportsLayout"));
const InfrastructureReportsDashboardPage = lazy(() => import("./pages/dashboard/InfrastructureReportsDashboardPage"));
const InfrastructureReportsSectionPage = lazy(() => import("./pages/dashboard/InfrastructureReportsSectionPage"));

const adminDashboardFallback = (
  <div className="container py-5 text-muted small">Loading admin workspace…</div>
);

function App() {
  return (
    <Router>
      <Routes>
        {/* DEFAULT ROUTE → Redirect to home */}
        <Route path="/" element={<Navigate to="/home" replace />} />

        {/* PUBLIC ROUTES - No Header/Footer (Login Layout) */}
        <Route element={<LoginLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="accept-invitation" element={<AcceptInvitationPage />} />
          <Route path="activate-logistics-provider" element={<ActivateLogisticsProviderPage />} />
          <Route path="signup" element={<SignupPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* B2B Hub Home — custom layout (no default footer) */}
        <Route element={<HubHomeLayout />}>
          <Route path="home" element={<Home />} />
          <Route path="contact" element={<Contact />} />
          <Route path="my-cart/checkout" element={<CartCheckoutPage />} />
          <Route path="my-cart" element={<MyCartPage />} />
          <Route path="my-wishlist" element={<MyWishlistPage />} />
          <Route path="my-saved-lists/:listId" element={<MySavedListsPage />} />
          <Route path="my-saved-lists" element={<MySavedListsPage />} />
          <Route path="marketplace/products/:productId/checkout" element={<ProductMarketplaceCheckoutPage />} />
          <Route path="marketplace/products/:productId" element={<ProductMarketplaceDetailPage />} />
          <Route path="marketplace/products" element={<ProductMarketplacePage />} />
          <Route path="marketplace/auctions" element={<AuctionList />} />
          <Route path="auction" element={<AuctionList />} />
          <Route path="procurement-services/:serviceSlug" element={<ProcurementServicesPublicPage />} />
          <Route path="procurement-services" element={<ProcurementServicesPublicPage />} />
          <Route path="about-us" element={<About />} />
          <Route path="contact-us" element={<Navigate to="/contact" replace />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="refund-cancellation-policy" element={<RefundCancellationPolicy />} />
          <Route path="domain-redirection-policy" element={<DomainRedirectionPolicy />} />
          <Route path="terms-and-conditions" element={<PublicTermsPage />} />
          <Route path="help-center" element={<HelpCenter />} />
          <Route path="help-center/articles" element={<HelpCenterArticlesPage />} />
          <Route path="help-center/articles/:articleId" element={<HelpCenterArticleDetailPage />} />
          <Route path="help-center/topics/:topicId" element={<HelpCenterTopicPage />} />
          <Route path="help-center/raise-ticket" element={<RaiseSupportTicketPage />} />
          <Route path="help-center/ticket-created" element={<TicketCreatedSuccessPage />} />
          <Route path="help-center/feedback" element={<FeedbackPage />} />
          <Route path="help-center/feedback-submitted" element={<FeedbackSubmittedSuccessPage />} />
          <Route path="submit-dispute" element={<SubmitDisputePage />} />
          <Route path="feedback" element={<FeedbackPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="logistics-providers" element={<LogisticsProvidersBrowsePage />} />
          <Route path="about" element={<Navigate to="/about-us" replace />} />
          <Route path="terms" element={<Navigate to="/terms-and-conditions" replace />} />
          <Route path="privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="return-refund-policy" element={<Navigate to="/refund-cancellation-policy" replace />} />
        </Route>

        {/* PUBLIC WEBSITE ROUTES - No Header/Footer (Public Layout) - No Auth Required */}
        <Route element={<PublicLayout />}>
          <Route path="contract-manufacturers/profile/:companyId" element={<ContractManufacturerPublicProfile />} />
          <Route path="contract-manufacturers/rfq/view" element={<ContractManufacturerRfqSubmittedViewPage />} />
          <Route path="contract-manufacturers/rfq/:companyId" element={<ContractManufacturerRfqPage />} />
          <Route path="contract-manufacturers/rfq" element={<ContractManufacturerRfqPage />} />
          <Route path="contract-manufacturers" element={<ContractManufacturersBrowsePage />} />
          <Route path="contract-manufacturers/:typeSlug" element={<ContractManufacturersBrowsePage />} />
          <Route path="logistics-providers/profile/:companyId" element={<LogisticsProviderProfileView />} />
          <Route path="logistics-providers/rfq/view" element={<LogisticsRfqSubmittedViewPage />} />
          <Route path="logistics-providers/rfq/:rfqKind" element={<LogisticsRfqPlaceholderPage />} />
          <Route path="logistics/rfq" element={<LogisticsRfqSelectionPage />} />
          <Route path="logistics/rfq/generic/providers" element={<LogisticsRfqMatchProvidersPage rfqKind="generic" />} />
          <Route
            path="logistics/rfq/business-relocation/providers"
            element={<LogisticsRfqMatchProvidersPage rfqKind="business-relocation" />}
          />
          <Route
            path="logistics/rfq/exhibition-logistics/providers"
            element={<LogisticsRfqMatchProvidersPage rfqKind="exhibition-logistics" />}
          />
          <Route path="logistics/rfq/:rfqKind" element={<LogisticsRfqPlaceholderPage />} />
          <Route path="infrastructure-leasing/post-requirement" element={<InfrastructureLeasingPostRequirementPage />} />
          <Route path="infrastructure-leasing/listing/:id" element={<InfrastructureLeasingPublicListingPage />} />
          <Route path="infrastructure-leasing" element={<Navigate to="/infrastructure-leasing/browse" replace />} />
          <Route path="infrastructure-leasing/browse" element={<InfrastructureLeasingBrowsePage />} />
          <Route path="infrastructure-leasing/search" element={<InfrastructureLeasingBrowsePage />} />
          <Route path="infrastructure-leasing/listings" element={<InfrastructureLeasingSearchListingsPage />} />
        </Route>

        {/* PUBLIC WEBSITE ROUTES - With Header/Footer (Website Layout) - No Auth Required */}
        <Route element={<AuthenticatedLayout />}>
          <Route path="about" element={<Navigate to="/about-us" replace />} />
        </Route>

        {/* PROTECTED ROUTES - With Header/Footer (Authenticated Layout) */}
        <Route
          element={
            <ProtectedRoute>
              <AuthenticatedLayout />
            </ProtectedRoute>
          }
        >

          {/* Dashboard Routes */}
          <Route path="dashboard/buyer" element={<BuyerDashboard />} />
          <Route path="dashboard/buyer/rfq" element={<BuyerDashboard moduleKey="rfq" />} />
          <Route path="dashboard/buyer/cm" element={<BuyerDashboard moduleKey="cm" />} />
          <Route path="dashboard/buyer/infra" element={<BuyerDashboard moduleKey="infra" />} />
          <Route path="dashboard/buyer/p2p" element={<BuyerDashboard moduleKey="p2p" />} />
          <Route path="dashboard/buyer/logistics" element={<BuyerDashboard moduleKey="logistics" />} />
          <Route path="dashboard/buyer/qa" element={<BuyerDashboard moduleKey="qa" />} />
          <Route path="dashboard/buyer/auctions" element={<BuyerDashboard moduleKey="auctions" />} />
          <Route path="dashboard/seller" element={<SellerDashboard />} />
          <Route path="dashboard/seller/rfq" element={<SellerDashboard moduleKey="rfq" />} />
          <Route path="dashboard/seller/cm" element={<SellerDashboard moduleKey="cm" />} />
          <Route path="dashboard/seller/p2p" element={<SellerDashboard moduleKey="p2p" />} />
          <Route path="dashboard/seller/infra" element={<SellerDashboard moduleKey="infra" />} />
          <Route path="dashboard/seller/logistics" element={<SellerDashboard moduleKey="logistics" />} />
          <Route path="dashboard/seller/auctions" element={<SellerDashboard moduleKey="auctions" />} />
          <Route path="dashboard/seller/fulfilment" element={<SellerDashboard moduleKey="fulfilment" />} />
          <Route
            path="dashboard/admin/infrastructure-disputes"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <InfrastructureLeasingAdminDisputesPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/admin/module-logistics-config"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <ModuleLogisticsIntegrationConfigPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/admin/p2p-organization-setup"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCompanyAdmin>
                    <P2POrganizationSetupPage />
                  </RequireCompanyAdmin>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/admin/p2p-approval-setup"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <P2PApprovalSetupRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/p2p/my-approvals"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCompanyApprover>
                    <MyApprovalsPage />
                  </RequireCompanyApprover>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/notifications"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <NotificationsPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support/disputes"
            element={
              <RouteErrorBoundary>
                <SupportAssignedDisputesPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support/disputes/:disputeId"
            element={
              <RouteErrorBoundary>
                <SupportDisputeDetailPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support/tickets"
            element={
              <RouteErrorBoundary>
                <SupportAssignedTicketsPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support/tickets/:ticketId"
            element={
              <RouteErrorBoundary>
                <SupportTicketDetailPage
                  mode="support"
                  backPath="/dashboard/support/tickets"
                  backLabel="My Assigned Tickets"
                />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support/feedback"
            element={
              <RouteErrorBoundary>
                <SupportAssignedFeedbackPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support/feedback/:feedbackId"
            element={
              <RouteErrorBoundary>
                <SupportFeedbackDetailPage
                  mode="support"
                  backPath="/dashboard/support/feedback"
                  backLabel="My Assigned Feedback"
                />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/disputes"
            element={
              <RouteErrorBoundary>
                <SuperAdminDisputeManagementPage
                  basePath="/dashboard/support-manager"
                  canAssign
                />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/disputes/:disputeId"
            element={
              <RouteErrorBoundary>
                <SuperAdminAssignedDisputeDetailPage backPath="/dashboard/support-manager/disputes" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/tickets"
            element={
              <RouteErrorBoundary>
                <SuperAdminTicketManagementPage
                  basePath="/dashboard/support-manager"
                  canAssign
                />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/tickets/:ticketId"
            element={
              <RouteErrorBoundary>
                <SupportTicketDetailPage
                  mode="manager"
                  backPath="/dashboard/support-manager/tickets"
                  backLabel="All Tickets"
                />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/feedback"
            element={
              <RouteErrorBoundary>
                <SuperAdminFeedbackManagementPage
                  basePath="/dashboard/support-manager"
                  canAssign
                />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/feedback/:feedbackId"
            element={
              <RouteErrorBoundary>
                <SupportFeedbackDetailPage
                  mode="manager"
                  backPath="/dashboard/support-manager/feedback"
                  backLabel="All Feedback"
                />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/roles"
            element={
              <RouteErrorBoundary>
                <SuperAdminRoleManagementPage basePath="/dashboard/support-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/roles/create"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateRolePage basePath="/dashboard/support-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/roles/:roleId/edit"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateRolePage basePath="/dashboard/support-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/users"
            element={
              <RouteErrorBoundary>
                <SuperAdminUserManagementPage basePath="/dashboard/support-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/users/create"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateUserPage basePath="/dashboard/support-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/support-manager/users/:userId/edit"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateUserPage basePath="/dashboard/support-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager"
            element={<Navigate to="/dashboard/logistics-manager/business-relocation-rfqs" replace />}
          />
          <Route
            path="dashboard/logistics-manager/providers"
            element={
              <RouteErrorBoundary>
                <LogisticsProvidersBrowsePage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/rfqs"
            element={
              <RouteErrorBoundary>
                <LogisticsRfqSentListPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/business-relocation-rfqs"
            element={
              <RouteErrorBoundary>
                <BusinessRelocationRfqsManagementPage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/exhibition-logistics-rfqs"
            element={
              <RouteErrorBoundary>
                <ExhibitionLogisticsRfqsManagementPage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/invite-providers"
            element={
              <RouteErrorBoundary>
                <InviteLogisticsProvidersPage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/roles"
            element={
              <RouteErrorBoundary>
                <SuperAdminRoleManagementPage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/roles/create"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateRolePage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/roles/:roleId/edit"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateRolePage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/users"
            element={
              <RouteErrorBoundary>
                <SuperAdminUserManagementPage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/users/create"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateUserPage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/logistics-manager/users/:userId/edit"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateUserPage basePath="/dashboard/logistics-manager" />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/help-center/my-disputes"
            element={
              <RouteErrorBoundary>
                <MyDisputesPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/help-center/my-disputes/:disputeId"
            element={
              <RouteErrorBoundary>
                <MyDisputeDetailPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/help-center/my-tickets"
            element={
              <RouteErrorBoundary>
                <MyTicketsPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/help-center/my-tickets/:ticketId"
            element={
              <RouteErrorBoundary>
                <MyTicketDetailPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/help-center/my-feedback"
            element={
              <RouteErrorBoundary>
                <MyFeedbackPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/help-center/my-feedback/:feedbackId"
            element={
              <RouteErrorBoundary>
                <MyFeedbackDetailPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/p2p/command-centre"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCommandCentreAccess>
                    <ProcurementCommandCentrePage />
                  </RequireCommandCentreAccess>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/p2p/transactions/:id"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCommandCentreAccess>
                    <P2PTransactionDetailPage />
                  </RequireCommandCentreAccess>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/p2p/preapproved-vendors"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCommandCentreAccess>
                    <PreApprovedVendorsPage />
                  </RequireCommandCentreAccess>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/p2p/services"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireServiceRequestAccess>
                    <ProcurementServiceCataloguePage />
                  </RequireServiceRequestAccess>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route path="dashboard/admin/banner-management" element={<Navigate to="/super-admin/master-data/banner-management" replace />} />
          <Route path="dashboard/admin/category-management" element={<Navigate to="/super-admin/master-data/category-management" replace />} />
          <Route path="dashboard/admin/category-management/bulk-upload" element={<Navigate to="/super-admin/master-data/category-management/bulk-upload" replace />} />
          <Route path="dashboard/admin/location-management" element={<Navigate to="/super-admin/master-data/location-management" replace />} />
          <Route path="dashboard/admin/factory-master-management" element={<Navigate to="/super-admin/master-data/factory-master-management" replace />} />
          <Route path="dashboard/admin/auction-category-management" element={<Navigate to="/super-admin/master-data/auction-category-management" replace />} />
          <Route path="dashboard/admin/auction-category-management/bulk-upload" element={<Navigate to="/super-admin/master-data/auction-category-management/bulk-upload" replace />} />
          <Route path="dashboard/admin/cm-industry-type-management" element={<Navigate to="/super-admin/master-data/cm-industry-type-management" replace />} />
          <Route path="dashboard/admin/cm-industry-type-management/bulk-upload" element={<Navigate to="/super-admin/master-data/cm-industry-type-management/bulk-upload" replace />} />
          <Route path="dashboard/admin/facility-category-management" element={<Navigate to="/super-admin/master-data/facility-category-management" replace />} />
          <Route path="dashboard/admin/facility-category-management/bulk-upload" element={<Navigate to="/super-admin/master-data/facility-category-management/bulk-upload" replace />} />
          <Route path="dashboard/admin/product-bulk-upload" element={<Navigate to="/super-admin/master-data/product-bulk-upload" replace />} />
          <Route
            path="dashboard/admin/p2p-platform-admin-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireSuperAdmin>
                    <P2PPlatformAdminManagementPage />
                  </RequireSuperAdmin>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/admin/p2p-platform-services"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireP2PPlatformAdmin>
                    <PlatformServiceCatalogueAdminPage />
                  </RequireP2PPlatformAdmin>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/p2p/service-console"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequirePlatformServiceConsole>
                    <ProcurementServiceConsolePage />
                  </RequirePlatformServiceConsole>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/admin/p2p-diagnostics"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <P2PAccessDiagnosticsPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/p2p/insights"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <P2PInsightsPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/admin/module-logistics-smoke"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <ModuleLogisticsBrowserSmokePage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="dashboard/admin/infrastructure-operations"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <InfrastructureOperationsLayout />
                </Suspense>
              </RouteErrorBoundary>
            }
          >
            <Route index element={<InfrastructureOperationsDashboardPage />} />
            <Route path=":section" element={<InfrastructureOperationsSectionPage />} />
          </Route>
          <Route
            path="dashboard/admin/infrastructure-reports"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <InfrastructureReportsLayout />
                </Suspense>
              </RouteErrorBoundary>
            }
          >
            <Route index element={<InfrastructureReportsDashboardPage />} />
            <Route path=":section" element={<InfrastructureReportsSectionPage />} />
          </Route>
          <Route path="infrastructure-leasing/list-asset" element={<InfrastructureLeasingListAssetPage />} />
          <Route path="infrastructure-leasing/my-properties" element={<InfrastructureLeasingMyPropertiesPage />} />
          <Route
            path="infrastructure-leasing/my-properties/inventory-overview"
            element={<InfrastructureLeasingSellerInventoryOverviewPage />}
          />
          <Route
            path="infrastructure-leasing/my-properties/:listingId/inventory"
            element={<InfrastructureLeasingSellerInventoryHubPage />}
          />
          <Route
            path="infrastructure-leasing/my-properties/:listingId/enquiries"
            element={<InfrastructureLeasingSellerListingEnquiriesPage />}
          />
          <Route
            path="infrastructure-leasing/my-properties/:listingId/enquiries/:enquiryId/buyer-requirement"
            element={<InfrastructureLeasingSellerBuyerRequirementViewPage />}
          />
          <Route
            path="infrastructure-leasing/my-properties/:listingId/enquiries/:enquiryId/payment-invoice"
            element={<InfrastructureLeasingSellerPaymentInvoicePage />}
          />
          <Route
            path="infrastructure-leasing/my-properties/:listingId/enquiries/:enquiryId/agreement"
            element={<InfrastructureLeasingAgreementDetailsPage />}
          />
          <Route
            path="infrastructure-leasing/my-enquiries/:listingId/:enquiryId/agreement"
            element={<InfrastructureLeasingAgreementDetailsPage />}
          />
          <Route
            path="infrastructure-leasing/my-enquiries/:listingId/:enquiryId/payment"
            element={<InfrastructureLeasingBuyerPaymentPage />}
          />
          <Route
            path="infrastructure-leasing/my-enquiries/:listingId/:enquiryId/invoice"
            element={<InfrastructureLeasingBuyerInvoicePage />}
          />
          <Route
            path="infrastructure-leasing/my-enquiries/:listingId/:enquiryId/lease-tracking"
            element={<InfrastructureLeasingBuyerLeaseTrackingPage />}
          />
          <Route path="infrastructure-leasing/my-enquiries" element={<InfrastructureLeasingBuyerEnquiriesPage />} />
          <Route path="infrastructure-leasing/my-requirements" element={<InfrastructureLeasingBuyerRequirementsPage />} />
          <Route
            path="infrastructure-leasing/my-requirements/:requirementId"
            element={<InfrastructureLeasingBuyerRequirementDetailPage />}
          />
          <Route path="dashboard/logistics" element={<LogisticsProviderDashboard />} />
          <Route path="dashboard/logistics/module/rfqs" element={<LogisticsProviderDashboard moduleKey="rfqs" />} />
          <Route path="dashboard/logistics/module/quotations" element={<LogisticsProviderDashboard moduleKey="quotations" />} />
          <Route path="dashboard/logistics/module/contracts" element={<LogisticsProviderDashboard moduleKey="contracts" />} />
          <Route path="dashboard/logistics/module/deliveries" element={<LogisticsProviderDashboard moduleKey="deliveries" />} />
          <Route path="dashboard/logistics/module/invoices" element={<LogisticsProviderDashboard moduleKey="invoices" />} />
          <Route path="dashboard/logistics/module/performance" element={<LogisticsProviderDashboard moduleKey="performance" />} />
          <Route path="dashboard/logistics/rfqs" element={<LogisticsProviderRfqsPage />} />
          <Route path="dashboard/logistics/rfqs/view" element={<LogisticsRfqSubmittedViewPage />} />
          <Route path="dashboard/logistics/rfqs/:submissionId/quote" element={<LogisticsProviderQuoteSubmissionPage />} />
          <Route path="dashboard/logistics/rfqs/:submissionId/pickup/:quoteId" element={<LogisticsPickupConfirmationPage />} />
          <Route path="dashboard/logistics/rfqs/:submissionId/handover/:quoteId" element={<LogisticsHandOverPage />} />
          <Route
            path="dashboard/logistics/rfqs/:submissionId/transit/:manifestId"
            element={<LogisticsShipmentTransitPage />}
          />
          <Route
            path="dashboard/logistics/rfqs/:submissionId/generate-invoice"
            element={<LogisticsGenerateInvoicePage />}
          />
          <Route path="logistics-rfqs-sent" element={<LogisticsRfqSentListPage />} />
          <Route path="logistics/my-rfqs" element={<LogisticsRfqSentListPage />} />
          <Route
            path="logistics/rfq/submitted/:kind/:id"
            element={<LogisticsDedicatedRfqSubmittedPage />}
          />
          <Route path="logistics-rfqs-sent/:submissionId/quotes" element={<LogisticsCompareQuotesPage />} />
          <Route path="logistics-rfqs-sent/:submissionId/quotes/:quoteId" element={<LogisticsQuoteDetailsPage />} />
          <Route path="logistics-rfqs-sent/:submissionId/handover/:quoteId" element={<LogisticsHandOverPage />} />
          {/* Leading / so React Router 7 matches full paths reliably under the pathless layout */}
          <Route path="/dashboard/buyer/contract-manufacturers" element={<BuyerContractManufacturersPage />} />
          <Route path="/dashboard/buyer/contract-manufacturers/quotes" element={<BuyerContractManufacturerQuotesPage />} />
          <Route path="/dashboard/buyer/contract-manufacturers/agreements/create" element={<BuyerContractManufacturerAgreementCreatePage />} />
          <Route
            path="/dashboard/buyer/contract-manufacturers/agreements/sign/:rfqId"
            element={<BuyerContractManufacturerSignAgreementPage />}
          />
          <Route
            path="/dashboard/buyer/contract-manufacturers/quality-approval/:rfqId"
            element={<BuyerCmQualityApprovalPage />}
          />
          <Route
            path="/dashboard/buyer/contract-manufacturers/recruit-external-qa/:rfqId"
            element={<BuyerRecruitExternalQaPage />}
          />
          <Route path="seller/cm-rfqs" element={<SellerContractManufacturerRfqsPage />} />
          <Route path="seller/cm-rfqs/:rfqId/agreement" element={<SellerContractManufacturerAgreementPage />} />
          <Route path="seller/cm-rfqs/:rfqId/quote" element={<SellerContractManufacturerRfqQuotePage />} />
          <Route path="seller/cm-rfqs/:rfqId/internal-inspection-report" element={<SellerCmInternalInspectionReportPage />} />
          <Route path="seller/cm-rfqs/:rfqId/invoice" element={<SellerCmProductionInvoicePage />} />
          <Route path="seller/cm-rfqs/:rfqId/eway-bill" element={<SellerCmProductionEwayPage />} />
          <Route path="seller/cm-rfqs/:rfqId/eway-tracking" element={<SellerCmProductionEwayTrackingPage />} />
          <Route path="seller/cm-rfqs/:rfqId/shipping-manifest" element={<SellerCmProductionShippingManifestPage />} />
          <Route path="seller/cm-rfqs/:rfqId/review-grn" element={<SellerCmProductionReviewGrnPage />} />
          <Route path="seller/cm-rfqs/:rfqId/product-tracking" element={<BuyerCmProductTrackingPage />} />
          <Route
            path="/dashboard/buyer/contract-manufacturers/production/:rfqId/delivery-confirmation"
            element={<BuyerCmDeliveryConfirmationPage />}
          />
          <Route
            path="/dashboard/buyer/contract-manufacturers/production/:rfqId/product-tracking"
            element={<BuyerCmProductTrackingPage />}
          />
          <Route
            path="/dashboard/buyer/contract-manufacturers/production/:rfqId"
            element={<BuyerCmProductionWorkspacePage />}
          />

          {/* Profile Routes */}
          <Route path="profile/view" element={<ViewProfile />} />
          <Route path="seller-profile" element={<SellerProfile />} />
          <Route path="contract-manufacturer-profile" element={<ContractManufacturerProfile />} />
          <Route path="buyer-profile" element={<BuyerProfile />} />
          <Route path="buyer-seller-profile" element={<BuyerSellerProfile />} />
          <Route path="quality-inspector-profile" element={<QualityInspectorProfile />} />
          <Route path="logistics-profile" element={<LogisticsProviderProfileView />} />
          <Route path="logistics-profile/edit" element={<LogisticsProviderProfile />} />
          <Route path="change-password" element={<ChangePasswordPage />} />

          {/* Buyer Routes */}
          <Route path="buyer-rfq-form" element={<BuyerRFQForm />} />
          <Route path="buyer-rfq" element={<BuyerRfq />} />
          <Route path="buyer-clarification-list" element={<BuyerClarificationList />} />
          <Route path="seller-quotation-list" element={<SellerQuotationList />} />
          <Route path="seller-quotation-accept" element={<SellerQuotationAccept />} />
          <Route path="buyer-qa-request" element={<BuyerQARequest />} />
          <Route path="buyer/qa-quotations-list" element={<BuyerQAQuotationsList />} />
          <Route path="buyer/qa-inspection/:inspectionId/review" element={<BuyerQAInspectionReview />} />
          <Route path="quotation/:quotationId/qa-request" element={<QuotationQARequestSuccess />} />
          <Route path="sample-request" element={<SampleRequest />} />
          <Route path="po-creation" element={<POCreation />} />
          <Route path="purchase-order-list" element={<PurchaseOrderList />} />
          <Route path="po-status-tracking" element={<POStatusTracking />} />
          <Route path="edit-purchase-order" element={<EditPurchaseOrder />} />
          <Route path="shipping-manifest-list" element={<ShippingManifestList />} />
          <Route path="grn-manifest-list" element={<GRNManifestList />} />
          <Route path="buyer-goods-receipt-generate" element={<BuyerGoodsReceiptGenerate />} />
          <Route path="approve-invoice-list" element={<ApproveInvoiceList />} />
          {/* Standalone checkout UI (UPI / card / net banking) — also used after CM delivery acceptance */}
          <Route path="payment/gateway" element={<PaymentGateway />} />
          <Route path="eway-bill-tracking" element={<EWayBillTracking />} />

          {/* Seller Routes */}
          <Route path="seller-rfq-list" element={<SellerRFQList />} />
          <Route path="clarification-list" element={<ClarificationList />} />
          <Route path="submit-quotation-to-buyer" element={<SubmitQuotationToBuyer />} />
          <Route path="seller-purchase-order-list" element={<SellerPurchaseOrderList />} />
          <Route path="purchase-order-accept" element={<PurchaseOrderAccept />} />
          <Route path="generate-invoice" element={<GenerateInvoice />} />
          <Route path="invoice-list" element={<InvoiceList />} />
          <Route path="eway-generation-list" element={<EwayGenerationList />} />
          <Route path="eway-bill-generate" element={<EWayBillGenerate />} />
          <Route path="shipping-manifest-generation-list" element={<ShippingManifestGenerationList />} />
          <Route path="shipping-manifest-create" element={<ShippingManifestCreate />} />
          <Route path="approve-goods-receipt-list" element={<ApproveGoodsReceiptList />} />

          {/* Auction Routes */}
          <Route path="auction/create" element={<CreateAuction />} />
          <Route path="auction/my/auctions" element={<MyAuctions />} />
          <Route path="auction/my/bids" element={<MyBids />} />
          <Route path="auction/purchase-credits" element={<PurchaseCredits />} />
          <Route path="auction/purchase-credits/success" element={<CreditPurchaseSuccess />} />
          <Route path="auction/:auctionId" element={<AuctionDetail />} />
          <Route path="auction/:auctionId/bid/success" element={<BidSuccess />} />
          <Route path="auction/:auctionId/review-bids" element={<ReviewBids />} />
          <Route path="auction/:auctionId/winning-bid-review" element={<WinningBidReview />} />
          <Route path="auction/:auctionId/payment" element={<AuctionPayment />} />
          <Route path="auction/:auctionId/party-connection" element={<PartyConnection />} />
          <Route path="auction/:auctionId/proforma-invoice" element={<ProformaInvoice />} />
          <Route path="qa/inspection/:inspectionId/invoice" element={<ProformaInvoice />} />
          <Route path="qa/inspection/:inspectionId/payment" element={<QAEscrowPayment />} />
          <Route path="qa/inspection/:inspectionId/payment/success" element={<QAPaymentSuccess />} />
          <Route path="qa/inspection/:inspectionId/assignment" element={<QAAssignmentAccept />} />
          <Route path="auction/:auctionId/payment/process" element={<PaymentProcess />} />
          <Route path="auction/:auctionId/payment/gateway" element={<PaymentGateway />} />
          <Route path="auction/:auctionId/transaction-completion" element={<TransactionCompletion />} />
          <Route path="auction/:auctionId/collection-delivery" element={<CollectionDeliveryConfirmation />} />
          
          {/* Quality Evaluator Flow */}
          <Route path="auction/:auctionId/quality-evaluator-prompt" element={<QualityEvaluatorPrompt />} />
          <Route path="auction/:auctionId/request-inspection-quotation" element={<RequestInspectionQuotation />} />
          <Route path="auction/:auctionId/rfq-sent-success" element={<RFQSentSuccess />} />
          <Route path="auction/qa/dashboard" element={<QADashboard />} />
          <Route path="auction/qa/dashboard/module/open-requests" element={<QADashboard moduleKey="open_requests" />} />
          <Route path="auction/qa/dashboard/module/quotations" element={<QADashboard moduleKey="quotations" />} />
          <Route path="auction/qa/dashboard/module/inspections" element={<QADashboard moduleKey="inspections" />} />
          <Route path="auction/qa/dashboard/module/reports" element={<QADashboard moduleKey="reports" />} />
          <Route path="auction/qa/dashboard/module/completed" element={<QADashboard moduleKey="completed" />} />
          <Route path="auction/qa/dashboard/module/quality-metrics" element={<QADashboard moduleKey="quality_metrics" />} />
          <Route path="auction/qa/dashboard/requests" element={<QARequestsList />} />
          <Route path="auction/qa/dashboard/inspections" element={<QAInspectionsList />} />
          <Route path="auction/qa/dashboard/completed" element={<QACompletedList />} />
          <Route path="auction/qa/request/:qaRequestId" element={<QARequestQuotationDetail />} />
          <Route path="auction/qa/quotation-request/:qaRequestId" element={<QuotationFlowInspectorQuotation />} />
          <Route path="auction/inspection-rfq/:rfqId" element={<InspectionRFQDetails />} />
          <Route path="auction/:auctionId/review-quotations" element={<ReviewInspectorQuotations />} />
          <Route path="auction/:auctionId/inspection-initiated" element={<InspectionInitiated />} />
          <Route path="auction/inspection/:inspectionId/complete" element={<InspectionCompletion />} />
          <Route path="auction/:auctionId/accept-reject-product" element={<AcceptRejectProduct />} />
          <Route path="auction/:auctionId/product-rejected" element={<ProductRejected />} />
        </Route>

        {/* SUPER ADMIN — isolated auth & navigation (no user menus) */}
        <Route element={<SuperAdminLoginLayout />}>
          <Route path="super-admin/login" element={<SuperAdminLoginPage />} />
        </Route>

        <Route
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout />
            </SuperAdminProtectedRoute>
          }
        >
          <Route path="super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />
          <Route path="super-admin/dashboard" element={<SuperAdminDashboardPage />} />
          <Route path="super-admin/unauthorized" element={<SuperAdminUnauthorizedPage />} />
          <Route
            path="super-admin/users"
            element={
              <RouteErrorBoundary>
                <SuperAdminUserManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/users/create"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateUserPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/users/:userId/edit"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateUserPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/roles"
            element={
              <RouteErrorBoundary>
                <SuperAdminRoleManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/roles/create"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateRolePage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/roles/:roleId/edit"
            element={
              <RouteErrorBoundary>
                <SuperAdminCreateRolePage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/disputes"
            element={
              <RouteErrorBoundary>
                <SuperAdminDisputeManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/disputes/:disputeId"
            element={
              <RouteErrorBoundary>
                <SuperAdminAssignedDisputeDetailPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/tickets"
            element={
              <RouteErrorBoundary>
                <SuperAdminTicketManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/tickets/:ticketId"
            element={
              <RouteErrorBoundary>
                <SuperAdminTicketDetailPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/feedback"
            element={
              <RouteErrorBoundary>
                <SuperAdminFeedbackManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/feedback/:feedbackId"
            element={
              <RouteErrorBoundary>
                <SuperAdminFeedbackDetailPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/logistics/business-relocation-rfqs"
            element={
              <RouteErrorBoundary>
                <BusinessRelocationRfqsManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/logistics/exhibition-logistics-rfqs"
            element={
              <RouteErrorBoundary>
                <ExhibitionLogisticsRfqsManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/logistics/rfq/submitted/:kind/:id"
            element={
              <RouteErrorBoundary>
                <LogisticsDedicatedRfqSubmittedPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/cms"
            element={
              <RouteErrorBoundary>
                <SuperAdminCmsDashboardPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/cms/terms"
            element={
              <RouteErrorBoundary>
                <SuperAdminTermsManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/cms/pages/:pageSlug"
            element={
              <RouteErrorBoundary>
                <SuperAdminCmsPageEditorPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/home-section-management"
            element={
              <RouteErrorBoundary>
                <SuperAdminHomeSectionManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/service-quick-links"
            element={
              <RouteErrorBoundary>
                <SuperAdminServiceQuickLinksPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/trading-categories"
            element={
              <RouteErrorBoundary>
                <SuperAdminTradingCategoriesPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/featured-category-showcase"
            element={
              <RouteErrorBoundary>
                <SuperAdminFeaturedCategoryShowcasePage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/top-industries"
            element={
              <RouteErrorBoundary>
                <SuperAdminTopIndustriesPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/platform-logo"
            element={
              <RouteErrorBoundary>
                <SuperAdminPlatformLogoManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/auth-hero-image"
            element={
              <RouteErrorBoundary>
                <SuperAdminAuthHeroManagementPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/banner-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <BannerManagementPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/category-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <MasterCategoryAdminPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/category-management/bulk-upload"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <MasterCategoryBulkUploadPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/location-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <LocationMasterAdminPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/factory-master-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <FactoryMasterAdminPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/auction-category-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <AuctionCategoryAdminPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/auction-category-management/bulk-upload"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <AuctionCategoryBulkUploadPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/cm-industry-type-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <CmIndustryTypeAdminPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/cm-industry-type-management/bulk-upload"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <CmIndustryTypeBulkUploadPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/facility-category-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <FacilityCategoryAdminPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/facility-category-management/bulk-upload"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <FacilityCategoryBulkUploadPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/master-data/product-bulk-upload"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <ProductBulkUploadPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/platform-admin-management"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireSuperAdmin>
                    <P2PPlatformAdminManagementPage />
                  </RequireSuperAdmin>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/diagnostics"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <P2PAccessDiagnosticsPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/organization-setup"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCompanyAdmin>
                    <P2POrganizationSetupPage />
                  </RequireCompanyAdmin>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/approval-setup"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <P2PApprovalSetupRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/my-approvals"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCompanyApprover>
                    <MyApprovalsPage />
                  </RequireCompanyApprover>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/command-centre"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCommandCentreAccess>
                    <ProcurementCommandCentrePage />
                  </RequireCommandCentreAccess>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/transactions/:id"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCommandCentreAccess>
                    <P2PTransactionDetailPage />
                  </RequireCommandCentreAccess>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/preapproved-vendors"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireCommandCentreAccess>
                    <PreApprovedVendorsPage />
                  </RequireCommandCentreAccess>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/services"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireServiceRequestAccess>
                    <ProcurementServiceCataloguePage />
                  </RequireServiceRequestAccess>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/platform-services"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequireP2PPlatformAdmin>
                    <PlatformServiceCatalogueAdminPage />
                  </RequireP2PPlatformAdmin>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/service-console"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <RequirePlatformServiceConsole>
                    <ProcurementServiceConsolePage />
                  </RequirePlatformServiceConsole>
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="super-admin/p2p/insights"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={adminDashboardFallback}>
                  <P2PInsightsPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        </Route>

        {/* Planned features — Coming Soon (must stay before the global 404 catch-all) */}
        <Route path="/welcome" element={<ComingSoonPage pageKey="welcome" />} />

        {/* 404 Page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
